import React, { useEffect, useRef, useState } from "react";
import { formatDuration } from "./chatUtils";

// =====================================================
// VOICE RECORDER
// Handles microphone permission + recording of a voice message using the
// MediaRecorder API. Fully self-contained: it manages the stream, the timer
// and cleanup, and hands the finished audio Blob (plus its duration) back to
// the parent via onSend. onCancel discards everything. The parent owns the
// actual upload so retry/idempotency stays in one place.
//
// Cleanup is aggressive on purpose — a lingering getUserMedia stream keeps the
// browser mic indicator on, which users (rightly) find alarming.
// =====================================================

function VoiceRecorder({ onSend, onCancel }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const startedAtRef = useRef(0);

  // Stop the timer + release the mic. Safe to call multiple times.
  const teardown = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    setError("");
    cancelledRef.current = false;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Voice recording isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
        teardown();
        setRecording(false);
        setSeconds(0);
        if (cancelledRef.current) return;
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        // A tap-and-release can produce zero bytes. Previously neither callback
        // fired, so the composer stayed replaced by a dead recorder strip the
        // user could not get out of. Always hand control back to the parent.
        if (blob.size > 0) onSend(blob, Math.max(1, elapsed));
        else onCancel();
      };

      recorder.start();
      startedAtRef.current = Date.now();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(
        () => setSeconds((s) => s + 1),
        1000
      );
    } catch (err) {
      teardown();
      setError(
        err?.name === "NotAllowedError"
          ? "Microphone permission denied."
          : "Could not access the microphone."
      );
    }
  };

  // Kick off recording as soon as the recorder mounts (user already tapped 🎤).
  useEffect(() => {
    startRecording();
    return () => {
      // Component unmounting mid-recording → discard, never send.
      cancelledRef.current = true;
      try {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopAndSend = () => {
    cancelledRef.current = false;
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    } else {
      onCancel();
    }
  };

  const cancel = () => {
    cancelledRef.current = true;
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    } else {
      teardown();
    }
    onCancel();
  };

  if (error) {
    return (
      <div className="voice-recorder voice-recorder-error">
        <span>⚠ {error}</span>
        <button type="button" onClick={onCancel} className="voice-cancel-btn">
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="voice-recorder">
      <button
        type="button"
        onClick={cancel}
        className="voice-cancel-btn"
        title="Cancel"
      >
        🗑
      </button>

      <div className="voice-rec-status">
        <span className="voice-rec-dot" />
        <span className="voice-rec-time">{formatDuration(seconds)}</span>
        <span className="voice-rec-label">Recording…</span>
      </div>

      <button
        type="button"
        onClick={stopAndSend}
        className="voice-send-btn"
        title="Send voice message"
        disabled={!recording}
      >
        ➤
      </button>
    </div>
  );
}

export default VoiceRecorder;
